import type { QueryKey } from '@tanstack/react-query';
import {
  ColumnDataTypes,
  FilterOptionTypes,
  type ColumnConfig,
  type DynamicFilterOptionsPage,
  type FilterOptionsParams,
} from '@/components/DataTable';
import {
  StringOperatorsWithEmpty,
  EnumOperatorsWithEmpty,
  NumberOperatorsWithEmpty,
  DateOperatorsWithEmpty,
} from '@/components/shared/shadow-ai';
import type {
  RunningNowListItem,
  RunningNowFilterOptionField,
} from '@kirin/api-client/contracts/shadow-ai-running-now';
import { RUNNING_NOW_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/shadow-ai-running-now';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import { queryKeys } from '@/hooks/api/queryKeys';
import { RUNNING_NOW_FILTER_URL_KEYS } from './constants';
import {
  ToolNameCell,
  CategoryCell,
  UserCell,
  LoggedInUserCell,
  PidCell,
  PortCell,
  StartedAtCell,
} from './CellRenderers';
import {
  ToolNameFilterOption,
  RunningNowUserFilterOption,
  RunningNowCategoryFilterOption,
} from './FilterComponents';
import { fetchRunningNowFilterOptions } from './api';

export function createRunningNowColumns(
  organizationId: string,
  /** Omit on a user-scoped surface — the User cell then renders plain text. */
  onUserClick?: (
    userId: string,
    toolId: string,
    processId: string,
    userInfo?: { email?: string; name?: string }
  ) => void,
  showLoggedInUser = false,
  // KIR-3495: on the Shadow AI user-scope page the dropdowns must offer only
  // the values present in that member's rows, not the whole organization's.
  scopeUserId?: string
): ColumnConfig<RunningNowListItem>[] {
  const optionsQueryKey = (
    field: RunningNowFilterOptionField,
    params: FilterOptionsParams
  ): QueryKey => {
    const base = queryKeys.runningNow.filterOptions(
      organizationId,
      field,
      params
    );
    // Keep the org-wide key byte-identical when unscoped so the existing cache
    // entries stay valid.
    return scopeUserId ? [...base, { scopeUserId }] : [...base];
  };

  const fetchOptions = (
    field: RunningNowFilterOptionField,
    params: FilterOptionsParams
  ): Promise<DynamicFilterOptionsPage> =>
    fetchRunningNowFilterOptions(organizationId, field, params, scopeUserId);

  return [
    {
      id: 'name',
      header: 'Process',
      accessor: 'toolName',
      renderCell: ({ row }) => <ToolNameCell row={row} />,
      width: 220,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperatorsWithEmpty,
      statePersistence: {
        urlKey: RUNNING_NOW_FILTER_URL_KEYS[RUNNING_NOW_FILTERABLE_FIELDS.NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params => optionsQueryKey('name', params),
        queryFn: params => fetchOptions('name', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <ToolNameFilterOption name={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'category',
      header: 'Category',
      accessor: 'category',
      renderCell: ({ row }) => <CategoryCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'category',
      sortKey: 'cat',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          RUNNING_NOW_FILTER_URL_KEYS[RUNNING_NOW_FILTERABLE_FIELDS.CATEGORY],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params => optionsQueryKey('category', params),
        queryFn: params => fetchOptions('category', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        labelFormatter: getShadowAiCategoryLabel,
        render: (option, isSelected) => (
          <RunningNowCategoryFilterOption
            category={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    // KIR-3495: on a page already scoped to one member the User column is the
    // same value on every row — omit it entirely rather than repeat it.
    ...(scopeUserId
      ? []
      : [
          {
            id: 'user',
            header: 'User',
            accessor: 'userId',
            renderCell: ({ row }) => (
              <UserCell row={row} onUserClick={onUserClick} />
            ),
            width: 200,
            sortable: true,
            sortField: 'user',
            sortKey: 'u',
            filterable: true,
            dataType: ColumnDataTypes.ENUM,
            filterOperators: EnumOperatorsWithEmpty,
            statePersistence: {
              urlKey:
                RUNNING_NOW_FILTER_URL_KEYS[RUNNING_NOW_FILTERABLE_FIELDS.USER],
            },
            filterOptions: {
              type: FilterOptionTypes.DYNAMIC,
              getQueryKey: params => optionsQueryKey('user', params),
              queryFn: params => fetchOptions('user', params),
              queryConfig: { staleTime: 2 * 60 * 1000 },
              render: (option, isSelected) => (
                <RunningNowUserFilterOption
                  userId={option.value}
                  userName={(option.metadata?.name as string | null) ?? null}
                  userEmail={(option.metadata?.email as string | null) ?? null}
                  isSelected={isSelected}
                />
              ),
              tooltip: { render: option => option.label ?? option.value },
            },
          } satisfies ColumnConfig<RunningNowListItem>,
        ]),
    ...(showLoggedInUser
      ? [
          {
            id: 'loggedInUser',
            header: 'Logged-in User',
            accessor: 'loggedInUserEmail',
            renderCell: ({ row }) => <LoggedInUserCell row={row} />,
            width: 220,
            // KIR-3657: sort by the signed-in account (N/A last, backend NULLS
            // LAST); filter by account with a rich name+email dropdown.
            sortable: true,
            sortField: 'loggedInUser',
            sortKey: 'liu',
            filterable: true,
            dataType: ColumnDataTypes.STRING,
            filterOperators: StringOperatorsWithEmpty,
            statePersistence: {
              urlKey:
                RUNNING_NOW_FILTER_URL_KEYS[
                  RUNNING_NOW_FILTERABLE_FIELDS.LOGGED_IN_USER
                ],
            },
            filterOptions: {
              type: FilterOptionTypes.DYNAMIC,
              getQueryKey: params => optionsQueryKey('loggedInUser', params),
              queryFn: params => fetchOptions('loggedInUser', params),
              pagination: { mode: 'offset', limit: 10 },
              queryConfig: { staleTime: 2 * 60 * 1000 },
              render: (option, isSelected) => (
                <RunningNowUserFilterOption
                  userId={option.value}
                  userName={(option.metadata?.name as string | null) ?? null}
                  userEmail={(option.metadata?.email as string | null) ?? null}
                  isSelected={isSelected}
                />
              ),
            },
          } satisfies ColumnConfig<RunningNowListItem>,
        ]
      : []),
    {
      id: 'pid',
      header: 'PID',
      accessor: 'pid',
      renderCell: ({ row }) => <PidCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'pid',
      sortKey: 'pid',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey: RUNNING_NOW_FILTER_URL_KEYS[RUNNING_NOW_FILTERABLE_FIELDS.PID],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'port',
      header: 'Port',
      accessor: 'port',
      renderCell: ({ row }) => <PortCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'port',
      sortKey: 'prt',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey: RUNNING_NOW_FILTER_URL_KEYS[RUNNING_NOW_FILTERABLE_FIELDS.PORT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'startedAt',
      header: 'Running For',
      accessor: 'startedAt',
      renderCell: ({ row }) => <StartedAtCell row={row} />,
      width: 130,
      sortable: true,
      sortField: 'startedAt',
      sortKey: 'sa',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          RUNNING_NOW_FILTER_URL_KEYS[RUNNING_NOW_FILTERABLE_FIELDS.STARTED_AT],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
